import LayoutContext from 'components/Layout/LayoutContext';
import InconsistenciaVotosModal, {
  IInconsistenciaVotosModalGenericProps,
} from 'components/Modal/__LEGACY__/InconsistenciaVotosModal/InconsistenciaVotosModal';
import {
  AssemblyModalitiesNumber,
  AssemblyStatus,
} from 'helpers/enums/Assembleia';
import { AssembleiaStatus } from 'helpers/enums/AssembleiaStatus';
import translation from 'locales/translation';
import {
  ButtonThemes,
  CheckCircleIcon,
  Col,
  Collapse,
  Modal,
  ModalSizes,
  Row,
  Select,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';

import { isNullOrUndefined } from 'helpers/utils/HTMLUtils';
import { getUniqueKey } from 'helpers/utils/KeyUtils';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IReducer } from 'store/Reducer';
import {
  assembleiasAssessorActionsAlterarSituacaoAssembleia,
  assembleiasAssessorLoadEvento,
} from 'store/modules/AssembleiasAssessorActions';
import { modalsActionsOpen } from 'store/modules/ModalsActions';
import './styles.scss';

interface IProps extends ModalProps {
  event: any;
}

const INITIAL_STATUS = -1;

export const ChangeEventSituation = (props: IProps) => {
  const [status, setStatus] = useState<AssembleiaStatus | -1>(INITIAL_STATUS);
  const [salvando, setSalvando] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const assembleiasAssessor = useSelector(
    (state: IReducer) => state.assembleiasAssessor
  );

  const { showToast } = useContext(LayoutContext);
  const dispatch = useDispatch();

  const canCloseWithoutQuorum = useMemo((): boolean => {
    if (assembleiasAssessor.evento?.existeQuorum) {
      return true;
    } else {
      if (assembleiasAssessor.evento?.tipo === 'AGN') {
        if (
          assembleiasAssessor.evento.modo === 3 ||
          assembleiasAssessor.evento.modo === 4
        ) {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    }
  }, [assembleiasAssessor]);

  const listOptions = useCallback(() => {
    return canCloseWithoutQuorum
      ? [
          { value: AssemblyStatus.NAO_DISPONIVEL, label: 'Não disponível' },
          { value: AssemblyStatus.PRE, label: 'Preparação' },
          { value: AssemblyStatus.LIVE, label: 'Assembleia' },
          { value: AssemblyStatus.ENCERRADO, label: 'Encerrada' },
          {
            value: AssemblyStatus.ENCERRADO_SEM_QUORUM,
            label: 'Assembleia encerrada sem quórum',
          },
        ]
      : [
          { value: AssemblyStatus.NAO_DISPONIVEL, label: 'Não disponível' },
          { value: AssemblyStatus.PRE, label: 'Preparação' },
          { value: AssemblyStatus.LIVE, label: 'Assembleia' },
          { value: AssemblyStatus.ENCERRADO, label: 'Encerrada' },
        ];
  }, [canCloseWithoutQuorum]);

  const ruleWarning = useMemo(() => {
    const { evento } = assembleiasAssessor;

    if (
      evento?.modo === AssemblyModalitiesNumber.HIBRIDO_PRESENCIAL_DIGITAL &&
      [AssembleiaStatus.NaoDisponivel].includes(evento?.status)
    ) {
      if (!isNullOrUndefined(evento?.dataBloqueio)) {
        if (
          [AssemblyStatus.PRE, AssemblyStatus.LIVE].includes(
            status as AssemblyStatus
          )
        ) {
          return true;
        }
      } else {
        if ((status as AssemblyStatus) === AssemblyStatus.PRE) {
          return true;
        }
      }
    }

    return false;
  }, [assembleiasAssessor, status]);

  const confirmDisable = useMemo((): boolean => {
    if (ruleWarning) {
      setDisabled(true);
    } else {
      if (salvando || status === assembleiasAssessor.evento!.status) {
        return true;
      }
    }
    return false;
  }, [ruleWarning, salvando, status, assembleiasAssessor.evento]);

  const confirmed = useCallback(() => {
    dispatch(
      assembleiasAssessorLoadEvento(assembleiasAssessor.evento!.id, () => {
        setSalvando(false);
        props.onClose!(props.modalKey);
      })
    );
  }, [dispatch, assembleiasAssessor.evento, props.onClose, props.modalKey]);

  const confirm = () => {
    if (status > -1) {
      setSalvando(true);
      dispatch(
        assembleiasAssessorActionsAlterarSituacaoAssembleia(
          assembleiasAssessor.evento!.id,
          status as any,
          confirmed,
          (error) => {
            const message: string =
              error.response &&
              error.response.data &&
              error.response.data.message
                ? error.response.data.message
                : error.message;
            setSalvando(false);
            if (
              error.response?.data.message ===
              'statusEventoLive.errorModoHibridoInconsistente'
            ) {
              props.onClose!(props.modalKey);
              setTimeout(() => {
                openVotosInconsistentesModal();
              }, 150);
            } else {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label:
                  translation.Toasts.Errors.Keys[
                    `${message}` as keyof typeof translation.Toasts.Errors.Keys
                  ] ?? translation.Toasts.Errors.defaultAlternative,
              });
            }
          }
        )
      );
    }
  };

  const openVotosInconsistentesModal = useCallback(() => {
    props.onClose!(props.modalKey);
    const modalKey: string = getUniqueKey();
    dispatch(
      modalsActionsOpen(
        InconsistenciaVotosModal,
        {
          genericProps: {
            key: modalKey,
            idEvento: assembleiasAssessor.evento?.id,
          } as IInconsistenciaVotosModalGenericProps,
        },
        modalKey
      )
    );
  }, [props.onClose, props.modalKey, dispatch, assembleiasAssessor.evento?.id]);

  const handleCancel = useCallback(() => {
    if (ruleWarning) {
      setStatus(-1);
    } else {
      props.onClose!(props.modalKey);
    }
  }, [props.modalKey, props.onClose, ruleWarning]);

  useEffect(() => {
    if (status === INITIAL_STATUS) {
      setStatus(assembleiasAssessor.evento!.status);
    }
  }, [status, assembleiasAssessor.evento]);

  useEffect(() => {
    if (ruleWarning) {
      setDisabled(true);
      const el = document.querySelector(`.fill`) as HTMLElement;

      if (el) {
        el.style.animation = `fillToZero ${3000}ms linear forwards`;
      }

      return () => {
        setDisabled(true);
      };
    }
  }, [ruleWarning]);

  return (
    <Modal
      title={
        ruleWarning
          ? 'Aviso!'
          : translation.Assessor.Modal.ChangeEventSituation.title
      }
      preventEscExit={props.preventEscExit || false}
      preventMaskExit={props.preventMaskExit || false}
      modalKey={props.modalKey}
      mobileOn={props.mobileOn}
      onClose={props.onClose}
      size={ModalSizes.Large}
      className={'root'}
      footer={[
        {
          theme: ButtonThemes.Secondary,
          leftIcon: <TimesCircleIcon />,
          children:
            translation.Assessor.Modal.ChangeEventSituation.cancelButton,
          disabled: salvando,
          onClick: handleCancel,
        },
        {
          theme: ButtonThemes.Primary,
          leftIcon: <CheckCircleIcon />,
          children:
            translation.Assessor.Modal.ChangeEventSituation.confirmButton,
          disabled: ruleWarning ? disabled : confirmDisable,
          onClick: confirm,
          loading: salvando,
        },
      ]}
    >
      <Row className={'content'}>
        <Collapse opened={ruleWarning} animateOpacity>
          <div className={'bar'}>
            <div
              className={'fill'}
              onAnimationEnd={() => {
                setDisabled(false);
              }}
            />
          </div>
        </Collapse>
        <Collapse opened={!ruleWarning} animateOpacity>
          <Col noPadding>
            <Select
              data-testid={'select-change-situation'}
              label={'Situação'}
              placeholder={'teste'}
              options={listOptions()}
              value={status}
              onChange={(e: number) => {
                setStatus(e);
              }}
              disabled={salvando}
              clearButton={false}
            />
          </Col>
        </Collapse>
        <Collapse opened={ruleWarning} animateOpacity>
          <Col noPadding>
            <p>{translation.Assessor.Modal.WarningBlockEvent.description}</p>
          </Col>
        </Collapse>
      </Row>
    </Modal>
  );
};
